import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import Layout from "@/components/layout/Layout";
import { Play } from "lucide-react";
import { pageHead } from "@/lib/routeHead";
import { videoPagesListQuery, videoThumb } from "@/lib/videoPages";

export const Route = createFileRoute("/videos/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(videoPagesListQuery),
  head: () =>
    pageHead({
      title: "RV Videos: Tours, How-Tos & Tips",
      description: "Watch RV tours, how-to videos and maintenance tips from Rex & Sons RVs in Castle Hayne, NC — each with its own detailed page.",
      path: "/videos",
    }),
  component: VideosIndex,
});

function VideosIndex() {
  const { data: videos } = useSuspenseQuery(videoPagesListQuery);
  return (
    <Layout>
      <section className="container py-10">
        <h1 className="font-heading text-3xl font-bold text-foreground md:text-4xl">RV Videos</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Tours, how-tos and tips from the Rex &amp; Sons RVs team. Want everything? See the full{" "}
          <Link to="/video-gallery" className="text-primary underline">video gallery</Link>.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((v, i) => (
            <Link key={v.id} to="/videos/$slug" params={{ slug: v.slug }} className="group flex flex-col overflow-hidden rounded-xl border bg-card shadow-xs transition hover:shadow-md">
              <div className="relative aspect-video overflow-hidden bg-muted">
                <img src={videoThumb(v.youtube_id)} alt={v.title} width={480} height={360} loading={i < 3 ? "eager" : "lazy"} decoding="async" className="h-full w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg"><Play className="ml-0.5 h-5 w-5" fill="currentColor" /></span>
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                {v.category && <span className="text-xs font-semibold uppercase text-primary">{v.category}</span>}
                <h2 className="mt-1 font-heading text-base font-bold text-foreground group-hover:text-primary">{v.title}</h2>
                {v.summary && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{v.summary}</p>}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
